import React, { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react'
import PropTypes from 'prop-types'
import { ACTION } from './actions'
import { scheduleReducer, initialScheduleState } from './scheduleReducer'

const ScheduleDraftContext = createContext(null)

export const ScheduleDraftProvider = ({
  children,
  initialSchedules,
  initialScheduleIndex = 0,
  scheduleRevision = '',
}) => {
  const [state, dispatch] = useReducer(scheduleReducer, initialScheduleState, current => {
    const schedules = initialSchedules?.length ? initialSchedules : current.schedules
    const maxIndex = Math.max(0, schedules.length - 1)
    const index = Math.min(Math.max(0, Number(initialScheduleIndex) || 0), maxIndex)
    return { ...current, schedules, activeScheduleId: schedules[index]?.id || schedules[0]?.id || null }
  })

  const revisionRef = useRef(null)

  useEffect(() => {
    if (revisionRef.current === null) {
      revisionRef.current = scheduleRevision
      return
    }
    if (revisionRef.current === scheduleRevision) return
    if (state.saving) return

    revisionRef.current = scheduleRevision
    const schedules = initialSchedules?.length ? initialSchedules : []
    if (!schedules.length) return

    dispatch({ type: ACTION.hydrate, schedules })
  }, [initialSchedules, scheduleRevision, state.saving])

  const value = useMemo(() => ({ state, dispatch }), [state])
  return <ScheduleDraftContext.Provider value={value}>{children}</ScheduleDraftContext.Provider>
}

ScheduleDraftProvider.propTypes = {
  children: PropTypes.node,
  initialSchedules: PropTypes.array,
  initialScheduleIndex: PropTypes.number,
  scheduleRevision: PropTypes.string,
}

export const useScheduleDraft = () => {
  const ctx = useContext(ScheduleDraftContext)
  if (!ctx) {
    throw new Error('useScheduleDraft must be used within ScheduleDraftProvider')
  }
  return ctx
}
